import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  InputAdornment,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import { useSearchParams } from 'react-router-dom';
import config from '../../config';
import WorkspacePageHeader from '../../components/workspace/WorkspacePageHeader';
import { WorkspaceErrorState } from '../../components/workspace/WorkspaceStates';

export class CanonicalAPIError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = 'CanonicalAPIError';
    this.status = status;
    this.payload = payload;
    this.validation = payload?.validation || null;
  }
}

export async function canonicalRequest(fetchWithAuth, path, options = {}) {
  const headers = new Headers(options.headers || {});
  if (typeof options.body !== 'undefined' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const response = await fetchWithAuth(`${config.apiBaseUrl}${path}`, { ...options, headers });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new CanonicalAPIError(
      payload.error || payload.detail || `Request failed with status ${response.status}`,
      response.status,
      payload,
    );
  }
  return payload;
}

export function parseJSONObject(value, fieldLabel) {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return {};
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
      throw new Error('must be a JSON object');
    }
    return parsed;
  } catch (error) {
    throw new Error(`${fieldLabel} must be valid JSON: ${error.message}`);
  }
}

export function asPrettyJSON(value) {
  if (!value || typeof value !== 'object') {
    return '{}';
  }
  return JSON.stringify(value, null, 2);
}

export function tabSlug(label) {
  return String(label || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function useAddressableTabs(labels, defaultIndex = 0) {
  const [searchParams, setSearchParams] = useSearchParams();
  const slugs = React.useMemo(() => labels.map(tabSlug), [labels]);
  const requested = searchParams.get('tab');
  const requestedIndex = slugs.indexOf(requested);
  const value = requestedIndex >= 0 ? requestedIndex : defaultIndex;
  const setValue = React.useCallback((nextIndex) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (nextIndex === defaultIndex) next.delete('tab');
      else next.set('tab', slugs[nextIndex] || slugs[defaultIndex]);
      return next;
    }, { replace: true });
  }, [defaultIndex, setSearchParams, slugs]);
  return [value, setValue];
}

export function ValidationSummary({ validation, title = 'Readiness check' }) {
  if (!validation) {
    return null;
  }
  const errors = Array.isArray(validation.errors) ? validation.errors : [];
  const warnings = Array.isArray(validation.warnings) ? validation.warnings : [];
  if (errors.length === 0 && warnings.length === 0) {
    return <Alert severity="success">{title} passed.</Alert>;
  }
  return (
    <Stack spacing={1} role="status" aria-live="polite">
      {errors.length ? (
        <Alert severity="error">
          <Typography variant="subtitle2" gutterBottom>{title} found {errors.length} issue{errors.length === 1 ? '' : 's'}.</Typography>
          <Box component="ul" sx={{ my: 0, pl: 2.5 }}>
            {errors.map((issue) => (
              <li key={`${issue.code}-${issue.field}`}>
                <Typography variant="body2">{issue.message}{issue.field ? ` (${issue.field})` : ''}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      ) : null}
      {warnings.length ? (
        <Alert severity="warning">
          <Typography variant="subtitle2" gutterBottom>{warnings.length} recommendation{warnings.length === 1 ? '' : 's'}</Typography>
          <Box component="ul" sx={{ my: 0, pl: 2.5 }}>
            {warnings.map((issue) => (
              <li key={`${issue.code}-${issue.field}-${issue.message}`}>
                <Typography variant="body2">{issue.message}{issue.field ? ` (${issue.field})` : ''}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      ) : null}
    </Stack>
  );
}

export function EditorPageShell({
  backTo,
  eyebrow,
  title,
  subtitle,
  status,
  version,
  loading,
  saving,
  error,
  validation,
  onReload,
  onValidate,
  onSave,
  saveLabel = 'Save draft',
  children,
  extraActions,
  backLabel,
}) {
  const inferredBackLabel = backLabel || (backTo === '/products' ? 'Products' : backTo === '/listings' ? 'Listings' : backTo === '/designs' ? 'Designs' : 'Workspace');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 4 }}>
      <WorkspacePageHeader
        sticky
        eyebrow={eyebrow}
        title={title}
        description={subtitle}
        breadcrumbs={[{ label: inferredBackLabel, to: backTo }, { label: title }]}
        badges={(
          <>
              {status ? <Chip size="small" label={status} color={status === 'ready' || status === 'active' ? 'success' : 'default'} /> : null}
              {version ? <Chip size="small" variant="outlined" label={`Version ${version}`} /> : null}
              <Chip size="small" variant="outlined" color="primary" label="Local draft" />
          </>
        )}
        actions={(
          <>
          {extraActions}
          {onReload ? (
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onReload} disabled={loading || saving}>
              Reload
            </Button>
          ) : null}
          {onValidate ? <Button variant="outlined" onClick={onValidate} disabled={loading || saving}>Validate</Button> : null}
          <Button variant="contained" startIcon={saving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />} onClick={onSave} disabled={loading || saving}>
            {saving ? 'Saving…' : saveLabel}
          </Button>
          </>
        )}
      />

      {error ? <WorkspaceErrorState message={error} onRetry={onReload || undefined} /> : null}
      <ValidationSummary validation={validation} />
      {loading ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={52} />
            <Skeleton variant="rounded" height={240} />
          </Stack>
        </Paper>
      ) : children}
    </Box>
  );
}

export function SectionCard({ title, description, action, children, sx }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, ...sx }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'flex-start' }}>
          <Box>
            <Typography component="h2" variant="subtitle1">{title}</Typography>
            {description ? <Typography variant="body2" color="text.secondary">{description}</Typography> : null}
          </Box>
          {action}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}

export function EmptyState({ title, description, action }) {
  return (
    <Box sx={{ py: 5, px: 2, textAlign: 'center' }}>
      <Typography component="h2" variant="subtitle1">{title}</Typography>
      {description ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{description}</Typography> : null}
      {action ? <Box sx={{ mt: 2 }}>{action}</Box> : null}
    </Box>
  );
}

export function RemoteEntityAutocomplete({
  fetchWithAuth,
  endpoint,
  label,
  value,
  onChange,
  getOptionLabel,
  helperText,
  disabled,
  required,
  query = {},
}) {
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState(value ? [value] : []);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const queryKey = JSON.stringify(query || {});

  React.useEffect(() => {
    if (value?.id) {
      setOptions((current) => current.some((option) => option.id === value.id) ? current : [value, ...current]);
    }
  }, [value]);

  React.useEffect(() => {
    if (disabled) {
      return undefined;
    }
    let active = true;
    const handle = window.setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({ limit: '50', ...JSON.parse(queryKey) });
        if (inputValue.trim()) {
          params.set('q', inputValue.trim());
        }
        const payload = await canonicalRequest(fetchWithAuth, `${endpoint}?${params.toString()}`);
        if (!active) {
          return;
        }
        const items = Array.isArray(payload.items) ? payload.items : [];
        setOptions(value?.id && !items.some((item) => item.id === value.id) ? [value, ...items] : items);
      } catch (loadError) {
        if (active) {
          setError(loadError.message || 'Unable to load options');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(handle);
    };
  }, [disabled, endpoint, fetchWithAuth, inputValue, queryKey, value]);

  return (
    <Autocomplete
      value={value || null}
      onChange={(event, next) => onChange(next)}
      inputValue={inputValue}
      onInputChange={(event, next) => setInputValue(next)}
      options={options}
      loading={loading}
      disabled={disabled}
      filterOptions={(items) => items}
      getOptionLabel={(option) => getOptionLabel(option) || ''}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={Boolean(error)}
          helperText={error || helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

function GalleryThumbnail({ src, alt }) {
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return (
      <Stack
        aria-hidden="true"
        alignItems="center"
        justifyContent="center"
        sx={{ width: '100%', height: '100%', color: 'text.disabled' }}
      >
        <ImageNotSupportedOutlinedIcon sx={{ fontSize: 38 }} />
      </Stack>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
    />
  );
}

export function MediaGalleryPicker({
  items = [],
  selectedItems = [],
  onToggle,
  getItemID = (item) => item?.id || item?.source_url,
  getItemImage = (item) => item?.preview_url || item?.source_url,
  getItemTitle = (item, index) => item?.alt_text || `Mockup ${index + 1}`,
  getItemSubtitle = (item) => item?.provider || item?.source || '',
  emptyMessage = 'No mockups are available for this fulfillment offer.',
  disabled = false,
}) {
  const selectedOrder = new Map(selectedItems.map((item, index) => [getItemID(item), index]));

  return (
    <Stack spacing={1.25}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5} justifyContent="space-between" alignItems={{ sm: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Select mockups in the order they should appear. The first selection is featured.
        </Typography>
        <Typography variant="body2" color={selectedItems.length ? 'primary.main' : 'text.secondary'} aria-live="polite">
          {selectedItems.length} selected
        </Typography>
      </Stack>
      <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default' }}>
        {items.length ? (
          <Box
            role="group"
            aria-label="Mockup choices"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(170px, 100%), 1fr))',
              gap: 1.25,
              maxHeight: 620,
              overflowY: 'auto',
              p: 0.25,
            }}
          >
            {items.map((item, index) => {
              const itemID = getItemID(item);
              const selectionIndex = selectedOrder.get(itemID);
              const selected = typeof selectionIndex === 'number';
              const title = getItemTitle(item, index);
              const subtitle = getItemSubtitle(item, index);
              return (
                <Paper
                  key={itemID}
                  variant="outlined"
                  sx={{
                    minWidth: 0,
                    overflow: 'hidden',
                    borderColor: selected ? 'primary.main' : 'divider',
                    borderWidth: selected ? 2 : 1,
                    boxShadow: selected ? (theme) => `0 0 0 2px ${theme.palette.primary.main}20` : 'none',
                    transition: (theme) => theme.transitions.create(['border-color', 'box-shadow', 'transform'], { duration: theme.transitions.duration.shortest }),
                    '&:hover': { borderColor: 'primary.main', transform: disabled ? 'none' : 'translateY(-1px)' },
                  }}
                >
                  <ButtonBase
                    aria-pressed={selected}
                    aria-label={`${selected ? `Selected as mockup ${selectionIndex + 1}; remove` : 'Select'} ${title}`}
                    disabled={disabled}
                    onClick={() => onToggle(item)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      width: '100%',
                      height: '100%',
                      textAlign: 'left',
                      '&.Mui-focusVisible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: -3 },
                    }}
                  >
                    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1', bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', p: 1 }}>
                      <GalleryThumbnail src={getItemImage(item)} alt="" />
                      {selected ? (
                        <Box
                          aria-hidden="true"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            minWidth: 28,
                            height: 28,
                            px: 0.75,
                            borderRadius: 14,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            display: 'grid',
                            placeItems: 'center',
                            typography: 'caption',
                            fontWeight: 700,
                            boxShadow: 1,
                          }}
                        >
                          {selectionIndex + 1}
                        </Box>
                      ) : null}
                    </Box>
                    <Stack spacing={0.25} sx={{ p: 1.25, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap title={title}>{title}</Typography>
                      {subtitle ? <Typography variant="caption" color="text.secondary" noWrap title={subtitle}>{subtitle}</Typography> : null}
                    </Stack>
                  </ButtonBase>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <Stack alignItems="center" justifyContent="center" spacing={0.5} sx={{ minHeight: 180, p: 3, textAlign: 'center' }}>
            <ImageNotSupportedOutlinedIcon color="disabled" sx={{ fontSize: 42 }} />
            <Typography variant="subtitle2">{emptyMessage}</Typography>
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}

export function RemoteEntityGalleryPicker({
  fetchWithAuth,
  endpoint,
  label,
  value,
  onChange,
  getItemTitle,
  getItemSubtitle = () => '',
  getItemImage = () => '',
  renderItemMeta,
  helperText,
  emptyMessage = 'No matching items were found.',
  query = {},
  pageSize = 12,
  disabled = false,
}) {
  const [inputValue, setInputValue] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [reloadKey, setReloadKey] = React.useState(0);
  const helperID = React.useId();
  const queryKey = JSON.stringify(query || {});
  const normalizedPageSize = Math.max(1, Math.min(Number(pageSize) || 12, 100));
  const pageCount = Math.max(1, Math.ceil(total / normalizedPageSize));

  React.useEffect(() => {
    setPage(1);
  }, [endpoint, queryKey]);

  React.useEffect(() => {
    if (disabled) return undefined;

    let active = true;
    setLoading(true);
    setError('');
    const handle = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          limit: String(normalizedPageSize),
          offset: String((page - 1) * normalizedPageSize),
        });
        Object.entries(JSON.parse(queryKey)).forEach(([key, queryValue]) => {
          if (queryValue !== '' && queryValue !== null && typeof queryValue !== 'undefined') {
            params.set(key, String(queryValue));
          }
        });
        if (inputValue.trim()) params.set('q', inputValue.trim());

        const payload = await canonicalRequest(fetchWithAuth, `${endpoint}?${params.toString()}`);
        if (!active) return;

        const nextItems = Array.isArray(payload.items) ? payload.items : [];
        const nextTotal = Number.isFinite(Number(payload.total)) ? Number(payload.total) : nextItems.length;
        const nextPageCount = Math.max(1, Math.ceil(nextTotal / normalizedPageSize));
        setItems(nextItems);
        setTotal(nextTotal);
        if (page > nextPageCount) setPage(nextPageCount);
      } catch (loadError) {
        if (active) {
          setItems([]);
          setTotal(0);
          setError(loadError.message || `Unable to load ${label.toLowerCase()}.`);
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(handle);
    };
  }, [disabled, endpoint, fetchWithAuth, inputValue, label, normalizedPageSize, page, queryKey, reloadKey]);

  const firstItem = total === 0 ? 0 : ((page - 1) * normalizedPageSize) + 1;
  const lastItem = Math.min(page * normalizedPageSize, total);
  const selectedTitle = value ? getItemTitle(value) : '';

  return (
    <Stack spacing={1.5} aria-describedby={helperText ? helperID : undefined}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <TextField
          fullWidth
          type="search"
          size="small"
          label={`Search ${label.toLowerCase()}`}
          value={inputValue}
          disabled={disabled}
          onChange={(event) => {
            setInputValue(event.target.value);
            setPage(1);
          }}
          sx={{ maxWidth: { sm: 440 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="body2" color={value ? 'primary.main' : 'text.secondary'} aria-live="polite">
          {value ? <>Selected: <Box component="span" sx={{ fontWeight: 600 }}>{selectedTitle}</Box></> : `Choose one ${label.toLowerCase()}`}
        </Typography>
      </Stack>

      {helperText ? <Typography id={helperID} variant="caption" color="text.secondary">{helperText}</Typography> : null}
      {error ? (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => setReloadKey((current) => current + 1)}>Retry</Button>}>
          {error}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default' }}>
        {loading ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(160px, 100%), 1fr))', gap: 1.25 }} aria-label={`Loading ${label.toLowerCase()}`}>
            {Array.from({ length: Math.min(normalizedPageSize, 12) }, (_, index) => (
              <Paper key={index} variant="outlined" sx={{ overflow: 'hidden' }}>
                <Skeleton variant="rectangular" sx={{ aspectRatio: '4 / 3' }} />
                <Box sx={{ p: 1.25 }}><Skeleton width="78%" /><Skeleton width="54%" /></Box>
              </Paper>
            ))}
          </Box>
        ) : items.length ? (
          <Box
            role="group"
            aria-label={`${label} choices`}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(160px, 100%), 1fr))',
              gap: 1.25,
              maxHeight: 560,
              overflowY: 'auto',
              p: 0.25,
            }}
          >
            {items.map((item) => {
              const selected = item.id === value?.id;
              const title = getItemTitle(item) || 'Untitled';
              const subtitle = getItemSubtitle(item);
              return (
                <Paper
                  key={item.id}
                  variant="outlined"
                  sx={{
                    minWidth: 0,
                    height: '100%',
                    overflow: 'hidden',
                    borderColor: selected ? 'primary.main' : 'divider',
                    borderWidth: selected ? 2 : 1,
                    boxShadow: selected ? (theme) => `0 0 0 2px ${theme.palette.primary.main}20` : 'none',
                    transition: (theme) => theme.transitions.create(['border-color', 'box-shadow', 'transform'], { duration: theme.transitions.duration.shortest }),
                    '&:hover': { borderColor: 'primary.main', transform: disabled ? 'none' : 'translateY(-1px)' },
                  }}
                >
                  <ButtonBase
                    aria-pressed={selected}
                    aria-label={`${selected ? 'Selected' : 'Select'} ${title}`}
                    disabled={disabled}
                    onClick={() => onChange(item)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      justifyContent: 'flex-start',
                      width: '100%',
                      height: '100%',
                      textAlign: 'left',
                      '&.Mui-focusVisible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: -3 },
                    }}
                  >
                    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4 / 3', bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', p: 1 }}>
                      <GalleryThumbnail src={getItemImage(item)} alt="" />
                      {selected ? (
                        <CheckCircleRoundedIcon
                          color="primary"
                          aria-hidden="true"
                          sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper', borderRadius: '50%', boxShadow: 1 }}
                        />
                      ) : null}
                    </Box>
                    <Stack spacing={0.5} sx={{ p: 1.25, minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        title={title}
                        sx={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', lineHeight: 1.3 }}
                      >
                        {title}
                      </Typography>
                      {subtitle ? (
                        <Typography variant="caption" color="text.secondary" noWrap title={subtitle}>{subtitle}</Typography>
                      ) : null}
                      {renderItemMeta ? <Box sx={{ mt: 'auto', pt: 0.25 }}>{renderItemMeta(item)}</Box> : null}
                    </Stack>
                  </ButtonBase>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <Stack alignItems="center" justifyContent="center" spacing={0.5} sx={{ minHeight: 220, p: 3, textAlign: 'center' }}>
            <ImageNotSupportedOutlinedIcon color="disabled" sx={{ fontSize: 42 }} />
            <Typography variant="subtitle2">{emptyMessage}</Typography>
            {inputValue ? <Typography variant="body2" color="text.secondary">Try a broader search.</Typography> : null}
          </Stack>
        )}
      </Paper>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary" aria-live="polite">
          {loading ? 'Loading…' : total ? `Showing ${firstItem}–${lastItem} of ${total}` : 'No items'}
        </Typography>
        {pageCount > 1 ? (
          <Pagination
            count={pageCount}
            page={Math.min(page, pageCount)}
            onChange={(event, nextPage) => setPage(nextPage)}
            disabled={disabled || loading}
            size="small"
            color="primary"
            showFirstButton
            showLastButton
          />
        ) : null}
      </Stack>
    </Stack>
  );
}
